import dayjs from 'dayjs';

import { firstDayOfWindow, lastDayOfWindow } from 'services/TherapyGroupPreferenceHelpers';

// Rows in the Edit Client Preference dialog: one segment window each, with calendar days (ISO
// strings at the start of the day, as the date inputs expect) for Start and an inclusive End
// (null when open-ended). preferenceId is null for a row that has no saved window yet; isSaved
// marks rows the dialog opened with, and hasStarted the saved windows already in effect now.

const today = () => dayjs().startOf('day');

const rowFromWindow = (window) => ({
    rowId: window.preferenceId,
    preferenceId: window.preferenceId,
    isSaved: true,
    hasStarted: !dayjs(window.start).isAfter(dayjs()),
    segmentId: window.segmentId,
    start: firstDayOfWindow(window.start).toISOString(),
    end: lastDayOfWindow(window.end)?.toISOString() ?? null,
});

const windowStillOpen = (window) => !window.end || dayjs().isBefore(dayjs(window.end));

const unsavedSegmentRow = (segmentId) => ({
    rowId: `current-${segmentId}`,
    preferenceId: null,
    isSaved: true,
    hasStarted: true,
    segmentId,
    start: today().toISOString(),
    end: null,
});

// Every still-open window for this OID (current and not yet started), then any segment the
// preferences snapshot names that has no loaded window yet.
export const preferenceRowsFor = (oid, preferences, windows) => {
    const saved = windows
        .filter((window) => window.oid === oid && window.segmentId && windowStillOpen(window))
        .map(rowFromWindow);
    const covered = new Set(saved.map((row) => row.segmentId));
    const unsaved = Object.entries(preferences.segments)
        .filter(([segmentId, segmentOid]) => segmentOid === oid && !covered.has(segmentId))
        .map(([segmentId]) => unsavedSegmentRow(segmentId));
    return [...saved, ...unsaved];
};

// A new row starts on startDay when given (the viewed date in the future), otherwise today.
export const newPreferenceRow = (rowId, startDay = undefined) => ({
    rowId,
    preferenceId: null,
    isSaved: false,
    hasStarted: false,
    segmentId: null,
    start: startDay ? dayjs(startDay).startOf('day').toISOString() : today().toISOString(),
    end: null,
});

export const rowAppliesToday = (row) =>
    !dayjs(row.start).isAfter(today(), 'day') &&
    (!row.end || !dayjs(row.end).isBefore(today(), 'day'));

const endOrForever = (row) => (row.end ? dayjs(row.end) : dayjs('2199-12-31'));

// Start and End are whole days and End is inclusive, so rows sharing a day overlap.
const rowsOverlap = (a, b) =>
    a.segmentId === b.segmentId &&
    !dayjs(a.start).isAfter(endOrForever(b), 'day') &&
    !dayjs(b.start).isAfter(endOrForever(a), 'day');

export const hasOverlappingRows = (rows) =>
    rows.some(
        (row, index) =>
            row.segmentId && rows.slice(index + 1).some((other) => rowsOverlap(row, other)),
    );

export const startsBeforeToday = (day) => dayjs(day).isBefore(today(), 'day');

export const endsBeforeStart = (end, start) => dayjs(end).isBefore(start, 'day');

const startsByToday = (row) => !dayjs(row.start).isAfter(today(), 'day');

const sameDay = (a, b) => (a && b ? dayjs(a).isSame(b, 'day') : a === b);

// The dates a kept saved row now has, when they differ from what it opened with.
const movedDates = (initial, row) => ({
    ...(sameDay(initial.start, row.start) ? {} : { start: row.start }),
    ...(sameDay(initial.end, row.end) ? {} : { end: row.end }),
});

// The requests one dialog save needs, as preference changes:
// - the global default when it is newly checked;
// - removed rows: a window that has not started is cancelled by id, one in effect is ended now;
// - kept saved windows whose dates changed are moved by id (only the changed dates);
// - each new row replaces its segment with its window.
// A removed row whose segment gets a new row starting today is not ended separately, because
// that replace ends it and the two requests would race.
export const clientPreferenceChanges = ({
    oid,
    isCurrentGlobal,
    isGlobalDefault,
    initialRows,
    rows,
}) => {
    const keptById = new Map(rows.map((row) => [row.rowId, row]));
    const added = rows.filter((row) => !row.isSaved);
    const replacedToday = new Set(added.filter(startsByToday).map((row) => row.segmentId));
    const removed = initialRows.filter((row) => !keptById.has(row.rowId));
    const notYetStarted = (row) => row.preferenceId && !row.hasStarted;
    const moved = initialRows
        .filter((row) => row.preferenceId && keptById.has(row.rowId))
        .map((row) => ({ row, dates: movedDates(row, keptById.get(row.rowId)) }))
        .filter(({ dates }) => Object.keys(dates).length > 0);
    return [
        ...(isGlobalDefault && !isCurrentGlobal ? [{ target: 'global', oid }] : []),
        ...removed.filter(notYetStarted).map((row) => ({
            target: 'window',
            preferenceId: row.preferenceId,
            segmentId: row.segmentId,
            cancel: true,
        })),
        ...removed
            .filter((row) => !notYetStarted(row) && !replacedToday.has(row.segmentId))
            .map((row) => ({ target: 'segment', segmentId: row.segmentId, oid: null })),
        ...moved.map(({ row, dates }) => ({
            target: 'window',
            preferenceId: row.preferenceId,
            segmentId: row.segmentId,
            oid,
            ...dates,
        })),
        ...added.map((row) => ({
            target: 'segment',
            segmentId: row.segmentId,
            oid,
            start: row.start,
            end: row.end,
        })),
    ];
};
